import { reportError } from "@/utils/reportError";
import { trackLogout, trackDeleteAccount, trackBugReported } from "@/utils/analytics";
import LoadingScreen from "@/components/LoadingScreen";
import SelectionModal from "@/components/SelectionModal";
import { POSTAL_CODES } from "@/constants/postalCodes";
import {
  getAppLanguagePreference,
  setAppLanguage,
  type AppLanguage,
} from "@/i18n";
import { useCompanies } from "@/hooks/useCompanies";
import { deactivatePushTokensForDevice } from "@/hooks/usePushNotifications";
import { useProfile } from "@/contexts/ProfileContext";
import { supabase } from "@/utils/supabase";
import { openUrlSafely } from "@/utils/linking";
import { getAppVersionLabel } from "@/utils/appVersion";
import { formatDisplayName } from "@/utils/name";
import { Ionicons } from "@expo/vector-icons";
import { Image } from "expo-image";
import * as ImagePicker from "expo-image-picker";
import { LinearGradient } from "expo-linear-gradient";
import { Stack, useRouter } from "expo-router";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ActivityIndicator,
  Alert,
  FlatList,
  Modal,
  Platform,
  ScrollView,
  StatusBar,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";


export default function SettingsScreen() {
  const { t } = useTranslation();
  const router = useRouter();
  const { profile, loading: profileLoading, updateProfile } = useProfile();
  const { companies, loading: companiesLoading } = useCompanies();

  const [profileImage, setProfileImage] = useState<string | null>(null);
  const [editNameVisible, setEditNameVisible] = useState(false);
  const [editProfileVisible, setEditProfileVisible] = useState(false);
  const [deleteAccountVisible, setDeleteAccountVisible] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [activeField, setActiveField] = useState<"company" | "postalCode" | null>(null);

  // Sélecteur de langue : préférence persistée ('fr' | 'en' | null = auto).
  const [languageModalVisible, setLanguageModalVisible] = useState(false);
  const [languagePreference, setLanguagePreference] =
    useState<AppLanguage | null>(null);

  useEffect(() => {
    getAppLanguagePreference().then(setLanguagePreference);
  }, []);

  // Libellés des options de langue. « Français » / « English » restent des
  // endonymes (identiques dans les deux langues), seul « Automatique » varie.
  const languageLabels: Record<"auto" | AppLanguage, string> = {
    auto: t("settings.languageAuto"),
    fr: t("settings.languageFrench"),
    en: t("settings.languageEnglish"),
  };
  const currentLanguageLabel = languagePreference
    ? languageLabels[languagePreference]
    : languageLabels.auto;

  const handleLanguageSelect = async (label: string) => {
    const value: AppLanguage | null =
      label === languageLabels.fr
        ? "fr"
        : label === languageLabels.en
          ? "en"
          : null;
    setLanguagePreference(value);
    // changeLanguage déclenche le re-render i18next : l'UI bascule immédiatement.
    await setAppLanguage(value);
  };

  // Form state
  const [tempFirstName, setTempFirstName] = useState("");
  const [tempLastName, setTempLastName] = useState("");
  const [tempCompany, setTempCompany] = useState("");
  const [tempCompanyId, setTempCompanyId] = useState<string | null>(null);
  const [tempPostalCode, setTempPostalCode] = useState("");

  // Sync with profile when it loads
  useEffect(() => {
    if (profile) {
      setTempFirstName(profile.firstName);
      setTempLastName(profile.lastName);
      setTempCompany(profile.company || "");
      setTempCompanyId(profile.companyId || null);
      setTempPostalCode(profile.postalCode);
    }
  }, [profile]);

  const pickImage = async () => {
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      allowsEditing: true,
      aspect: [1, 1],
      quality: 1,
    });

    if (!result.canceled) {
      setProfileImage(result.assets[0].uri);
    }
  };

  const handleSaveName = async () => {
    if (!tempFirstName.trim() || !tempLastName.trim()) {
      Alert.alert(t("common.errorTitle"), t("settings.fillAllFields"));
      return;
    }

    const result = await updateProfile({
      firstName: tempFirstName,
      lastName: tempLastName,
    });

    if (result.success) {
      Alert.alert(t("common.successTitle"), t("settings.nameUpdated"));
      setEditNameVisible(false);
    } else {
      Alert.alert(t("common.errorTitle"), t("settings.nameUpdateError"));
    }
  };

  const handleSaveProfile = async () => {
    if (!tempCompany || !tempPostalCode) {
      Alert.alert(t("common.errorTitle"), t("settings.fillAllFields"));
      return;
    }

    const result = await updateProfile({
      company: tempCompany || null,
      companyId: tempCompanyId,
      postalCode: tempPostalCode,
    });

    if (result.success) {
      Alert.alert(t("common.successTitle"), t("editProfile.profileUpdated"));
      setEditProfileVisible(false);
    } else {
      Alert.alert(t("common.errorTitle"), t("editProfile.profileUpdateError"));
    }
  };

  const openModal = (field: "company" | "postalCode") => {
    // Close the edit profile modal first to avoid conflict
    setEditProfileVisible(false);
    
    // Then open the selection modal
    setActiveField(field);
    setModalVisible(true);
  };

  const handleSelect = (item: string) => {
    if (activeField === "company") {
      setTempCompany(item);
      // Find the company ID from the companies list
      const selectedCompany = companies.find(c => c.name === item);
      setTempCompanyId(selectedCompany?.id || null);
    } else if (activeField === "postalCode") {
      setTempPostalCode(item);
    }
    setModalVisible(false);
    setActiveField(null);
    
    // Reopen the edit profile modal
    setTimeout(() => setEditProfileVisible(true), 300);
  };

  const renderModalItem = ({ item }: { item: string }) => (
    <TouchableOpacity style={styles.modalItem} onPress={() => handleSelect(item)}>
      <Text style={styles.modalItemText}>{item}</Text>
    </TouchableOpacity>
  );

  const handleNotifications = () => {
    router.push("/settings-notifications");
  };

  const handleRankingVisibility = async () => {
    const currentValue = profile?.displayMainRanking ?? true;
    const newValue = !currentValue;
    
    Alert.alert(
      newValue ? t("settings.showRankingTitle") : t("settings.hideRankingTitle"),
      newValue
        ? t("settings.showRankingMessage")
        : t("settings.hideRankingMessage"),
      [
        { text: t("common.cancel"), style: "cancel" },
        {
          text: t("common.confirm"),
          onPress: async () => {
            try {
              // updateProfile persiste display_main_ranking en base ET rafraîchit
              // le contexte — inutile de doubler par un UPDATE direct.
              const { success } = await updateProfile({ displayMainRanking: newValue });
              if (success) {
                Alert.alert(
                  t("settings.visibilityChangedTitle"),
                  newValue
                    ? t("settings.visibleInRanking")
                    : t("settings.hiddenFromRanking")
                );
              }
            } catch (error) {
              reportError(error);
              Alert.alert(t("common.errorTitle"), t("settings.visibilityError"));
            }
          }
        }
      ]
    );
  };

  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        Alert.alert(t("common.errorTitle"), t("settings.sessionError"));
        return;
      }

      // Call the Edge Function to fully delete the account (RGPD/LPD)
      const { data, error } = await supabase.functions.invoke('delete-account', {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (error) {
        throw error;
      }

      // Sign out locally
      trackDeleteAccount();
      await supabase.auth.signOut();

      setDeleteAccountVisible(false);
      Alert.alert(
        t("settings.accountDeletedTitle"),
        t("settings.accountDeletedMessage"),
        [
          {
            text: t("common.ok"),
            onPress: () => router.replace("/login"),
          },
        ]
      );
    } catch (error) {
      reportError(error);
      Alert.alert(t("common.errorTitle"), t("settings.deleteError"));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleLogout = async () => {
    try {
      trackLogout();
      // Désactiver le token push de cet appareil AVANT signOut (la RLS bloque
      // l'update une fois la session perdue) → plus de notifs après déconnexion.
      await deactivatePushTokensForDevice();
      await supabase.auth.signOut();
      router.replace("/login");
    } catch (error) {
      reportError(error);
      router.replace("/login");
    }
  };

  if (profileLoading && !profile) {
    return (
      <>
        <Stack.Screen options={{ headerShown: false }} />
        <LoadingScreen message={t("common.loading")} />
      </>
    );
  }

  return (
    <>
      <Stack.Screen options={{ headerShown: false, animation: 'none' }} />
      <SafeAreaView
        style={{ flex: 1, backgroundColor: "#FBEFE3" }}
        edges={["top"]}
      >
        <StatusBar barStyle="dark-content" backgroundColor="#FBEFE3" />
        <LinearGradient
          colors={["#FBEFE3", "#FFFFFF"]}
          locations={[0, 1]}
          style={styles.container}
        >
          <ScrollView
            contentContainerStyle={styles.scrollContent}
            showsVerticalScrollIndicator={false}
          >
            {/* Header */}
            <View style={styles.header}>
              <TouchableOpacity
                style={styles.backButton}
                onPress={() => router.back()}
              >
                <Ionicons name="chevron-back" size={24} color="#000" />
              </TouchableOpacity>
              <Text style={styles.headerTitle}>{t("settings.headerTitle")}</Text>
              <View style={{ width: 40 }} />
            </View>

            {/* Profile Section */}
            <View style={styles.profileSection}>
              <TouchableOpacity
                onPress={() => router.push('/edit-profile')}
                style={styles.profileImageContainer}
              >
                {profile?.avatarUrl ? (
                  <Image
                    source={{ uri: profile.avatarUrl }}
                    style={styles.profileImage}
                    cachePolicy="memory-disk"
                    contentFit="cover"
                    transition={200}
                  />
                ) : (
                  <View style={styles.profileImagePlaceholder}>
                    <Ionicons name="person" size={60} color="#733801" />
                  </View>
                )}
                <View style={styles.editBadge}>
                  <Ionicons name="pencil" size={14} color="#FFF" />
                </View>
              </TouchableOpacity>

              <TouchableOpacity
                onPress={() => router.push('/edit-profile')}
              >
                <Text style={styles.userName}>
                  {formatDisplayName(profile?.firstName, profile?.lastName)}
                </Text>
              </TouchableOpacity>
              <Text style={styles.userEmail}>{profile?.email}</Text>
              {profile?.company && (
                <Text style={styles.userCompany}>{profile.company}</Text>
              )}
            </View>

            {/* Settings Menu */}
            <View style={styles.menuSection}>
              <TouchableOpacity
                style={styles.menuItem}
                onPress={() => router.push('/edit-profile')}
              >
                <Text style={styles.menuText}>{t("settings.editProfile")}</Text>
                <Ionicons name="chevron-forward" size={20} color="#666" />
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.menuItem}
                onPress={handleNotifications}
              >
                <Text style={styles.menuText}>{t("settings.notifications")}</Text>
                <Ionicons name="chevron-forward" size={20} color="#666" />
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.menuItem}
                onPress={() => setLanguageModalVisible(true)}
              >
                <View style={styles.menuItemContent}>
                  <Text style={styles.menuText}>{t("settings.language")}</Text>
                  <Text style={styles.menuSubtext}>{currentLanguageLabel}</Text>
                </View>
                <Ionicons name="chevron-forward" size={20} color="#666" />
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.menuItem}
                onPress={handleRankingVisibility}
              >
                <View style={styles.menuItemContent}>
                  <Text style={styles.menuText}>{t("settings.rankingVisibility")}</Text>
                  <Text style={styles.menuSubtext}>
                    {profile?.displayMainRanking
                      ? t("settings.enabled")
                      : t("settings.disabled")}
                  </Text>
                </View>
                <Ionicons name="chevron-forward" size={20} color="#666" />
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.menuItem}
                onPress={() => {
                  const subject = encodeURIComponent(t("settings.bugEmailSubject"));
                  const body = encodeURIComponent(
                    t("settings.bugEmailBody", {
                      // Version RÉELLE du binaire (numéro de build + commit) :
                      // sans ça un signalement est inexploitable, on ne sait pas
                      // sur quelle version le bug a été vu.
                      appVersion: getAppVersionLabel(),
                      platform: Platform.OS,
                      osVersion: Platform.Version,
                    })
                  );
                  trackBugReported();
                  openUrlSafely(
                    `mailto:app@repaschallenge.ch?subject=${subject}&body=${body}`,
                    {
                      title: t("common.noEmailAppTitle"),
                      message: t("settings.bugNoEmailAppMessage"),
                    }
                  );
                }}
              >
                <Text style={[styles.menuText, { color: '#CD7728' }]}>{t("settings.reportBug")}</Text>
                <Ionicons name="bug-outline" size={20} color="#CD7728" />
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.menuItem}
                onPress={() => setDeleteAccountVisible(true)}
              >
                <Text style={styles.menuTextDanger}>
                  {t("settings.deleteAccountMenu")}
                </Text>
                <Ionicons name="chevron-forward" size={20} color="#D32F2F" />
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.logoutButton}
                onPress={handleLogout}
              >
                <Text style={styles.logoutText}>{t("settings.logout")}</Text>
                <Ionicons name="log-out-outline" size={20} color="#D32F2F" />
              </TouchableOpacity>
            </View>

            <Text style={styles.versionText} selectable>
              REPAS v{getAppVersionLabel()}
            </Text>
          </ScrollView>

          {/* Edit Name Modal */}
          <Modal
            animationType="fade"
            transparent={true}
            visible={editNameVisible}
            onRequestClose={() => setEditNameVisible(false)}
          >
            <View style={styles.modalContainer}>
              <TouchableOpacity
                style={styles.modalOverlay}
                activeOpacity={1}
                onPress={() => setEditNameVisible(false)}
              >
                <View
                  style={styles.modalContent}
                  onStartShouldSetResponder={() => true}
                >
                  <Text style={styles.modalTitle}>{t("settings.editNameTitle")}</Text>
                  <TextInput
                    style={styles.input}
                    value={tempFirstName}
                    onChangeText={setTempFirstName}
                    placeholder={t("editProfile.firstNamePlaceholder")}
                    placeholderTextColor="#999"
                  />
                  <TextInput
                    style={styles.input}
                    value={tempLastName}
                    onChangeText={setTempLastName}
                    placeholder={t("editProfile.lastNamePlaceholder")}
                    placeholderTextColor="#999"
                  />
                  <View style={styles.modalButtons}>
                    <TouchableOpacity
                      style={styles.cancelButton}
                      onPress={() => setEditNameVisible(false)}
                    >
                      <Text style={styles.cancelButtonText}>{t("common.cancel")}</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={styles.saveButton}
                      onPress={handleSaveName}
                      disabled={profileLoading}
                    >
                      <LinearGradient
                        colors={["#1C453F", "#78A584"]}
                        start={{ x: 0, y: 0 }}
                        end={{ x: 1, y: 0 }}
                        style={styles.saveButtonGradient}
                      >
                        {profileLoading ? (
                          <ActivityIndicator color="#FFF" />
                        ) : (
                          <Text style={styles.saveButtonText}>{t("common.save")}</Text>
                        )}
                      </LinearGradient>
                    </TouchableOpacity>
                  </View>
                </View>
              </TouchableOpacity>
            </View>
          </Modal>

          {/* Edit Profile Modal */}
          <Modal
            animationType="fade"
            transparent={true}
            visible={editProfileVisible}
            onRequestClose={() => setEditProfileVisible(false)}
          >
            <View style={styles.modalContainer}>
              <TouchableOpacity
                style={styles.modalOverlay}
                activeOpacity={1}
                onPress={() => setEditProfileVisible(false)}
              >
                <View
                  style={styles.modalContent}
                  onStartShouldSetResponder={() => true}
                >
                  <Text style={styles.modalTitle}>{t("settings.editProfile")}</Text>

                  <Text style={styles.inputLabel}>{t("editProfile.companyLabel")}</Text>
                  <TextInput
                    style={styles.input}
                    value={tempCompany}
                    onChangeText={(text) => {
                      setTempCompany(text);
                      // Champ libre ; on conserve l'id si le nom correspond à une
                      // entreprise connue (classement entreprise préservé).
                      const match = companies.find(
                        (c) => c.name.trim().toLowerCase() === text.trim().toLowerCase()
                      );
                      setTempCompanyId(match?.id ?? null);
                    }}
                    placeholder={t("editProfile.companyPlaceholder")}
                    placeholderTextColor="#999"
                  />

                  <Text style={styles.inputLabel}>{t("settings.postalCodeLabel")}</Text>
                  <TouchableOpacity
                    style={styles.selectInput}
                    onPress={() => openModal("postalCode")}
                  >
                    <Text
                      style={[
                        styles.selectInputText,
                        tempPostalCode ? styles.selectInputTextSelected : {},
                      ]}
                    >
                      {tempPostalCode || t("editProfile.selectPostalCode")}
                    </Text>
                    <Ionicons name="chevron-down" size={20} color="#999" />
                  </TouchableOpacity>

                  <View style={styles.modalButtons}>
                    <TouchableOpacity
                      style={styles.cancelButton}
                      onPress={() => setEditProfileVisible(false)}
                    >
                      <Text style={styles.cancelButtonText}>{t("common.cancel")}</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={styles.saveButton}
                      onPress={handleSaveProfile}
                      disabled={profileLoading}
                    >
                      <LinearGradient
                        colors={["#1C453F", "#78A584"]}
                        start={{ x: 0, y: 0 }}
                        end={{ x: 1, y: 0 }}
                        style={styles.saveButtonGradient}
                      >
                        {profileLoading ? (
                          <ActivityIndicator color="#FFF" />
                        ) : (
                          <Text style={styles.saveButtonText}>{t("common.save")}</Text>
                        )}
                      </LinearGradient>
                    </TouchableOpacity>
                  </View>
                </View>
              </TouchableOpacity>
            </View>
          </Modal>

          {/* Delete Account Modal */}
          <Modal
            animationType="fade"
            transparent={true}
            visible={deleteAccountVisible}
            onRequestClose={() => setDeleteAccountVisible(false)}
          >
            <View style={styles.modalContainer}>
              <TouchableOpacity
                style={styles.modalOverlay}
                activeOpacity={1}
                onPress={() => setDeleteAccountVisible(false)}
              >
                <View
                  style={styles.modalContent}
                  onStartShouldSetResponder={() => true}
                >
                  <Text style={styles.modalTitle}>{t("settings.deleteAccountTitle")}</Text>
                  <Text style={styles.modalDescription}>
                    {t("settings.deleteAccountMessage")}
                  </Text>
                  <View style={styles.modalButtons}>
                    <TouchableOpacity
                      style={styles.cancelButton}
                      onPress={() => setDeleteAccountVisible(false)}
                      disabled={isDeleting}
                    >
                      <Text style={styles.cancelButtonText}>{t("common.cancel")}</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[styles.deleteButton, isDeleting && styles.deleteButtonDisabled]}
                      onPress={handleDeleteAccount}
                      disabled={isDeleting}
                    >
                      {isDeleting ? (
                        <ActivityIndicator color="#FFF" size="small" />
                      ) : (
                        <Text style={styles.deleteButtonText}>{t("common.delete")}</Text>
                      )}
                    </TouchableOpacity>
                  </View>
                </View>
              </TouchableOpacity>
            </View>
          </Modal>

          {/* Selection Modal — pageSheet : carte sous la barre d'état (sinon le
              titre passait sous l'encoche, SafeAreaView ne s'applique pas dans
              un Modal plein écran). */}
          <Modal
            animationType="slide"
            transparent={false}
            presentationStyle="pageSheet"
            visible={modalVisible}
            onRequestClose={() => setModalVisible(false)}
          >
            <SafeAreaView style={{ flex: 1, backgroundColor: '#FFF' }} edges={['top', 'bottom']}>
              <View style={styles.fullModalContainer}>
                <View style={styles.fullModalHeader}>
                  <Text style={styles.fullModalTitle}>
                    {activeField === "company"
                      ? t("editProfile.selectCompanyTitle")
                      : t("editProfile.selectPostalCode")}
                  </Text>
                  <TouchableOpacity 
                    onPress={() => setModalVisible(false)}
                    style={{ padding: 8 }}
                  >
                    <Ionicons name="close" size={28} color="#000" />
                  </TouchableOpacity>
                </View>
                
                <FlatList
                  data={activeField === "company" ? companies.map(c => c.name) : POSTAL_CODES}
                  renderItem={renderModalItem}
                  keyExtractor={(item) => item}
                  showsVerticalScrollIndicator={true}
                  contentContainerStyle={{ padding: 16 }}
                />
              </View>
            </SafeAreaView>
          </Modal>

          {/* Sélecteur de langue — réutilise le pattern SelectionModal
              (pageSheet + coche sur l'option active). */}
          <SelectionModal
            visible={languageModalVisible}
            title={t("settings.language")}
            data={[languageLabels.auto, languageLabels.fr, languageLabels.en]}
            selectedValue={currentLanguageLabel}
            onSelect={handleLanguageSelect}
            onClose={() => setLanguageModalVisible(false)}
          />
        </LinearGradient>
      </SafeAreaView>
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollContent: {
    padding: 24,
    paddingTop: 0,
    paddingBottom: 24,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  loadingText: {
    fontFamily: "Inter_400Regular",
    fontSize: 16,
    color: "#666",
    marginTop: 12,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 32,
  },
  backButton: {
    width: 40,
    height: 40,
    alignItems: "center",
    justifyContent: "center",
  },
  headerTitle: {
    fontFamily: "Inter_700Bold",
    fontSize: 18,
    color: "#000",
    flex: 1,
    textAlign: "center",
  },
  profileSection: {
    alignItems: "center",
    marginBottom: 32,
  },
  profileImageContainer: {
    position: "relative",
    marginBottom: 16,
  },
  profileImage: {
    width: 120,
    height: 120,
    borderRadius: 60,
  },
  profileImagePlaceholder: {
    width: 120,
    height: 120,
    borderRadius: 60,
    backgroundColor: "#E0CCB8",
    alignItems: "center",
    justifyContent: "center",
  },
  editBadge: {
    position: "absolute",
    bottom: 0,
    right: 0,
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: "#CD7728",
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 3,
    borderColor: "#FFF",
  },
  userName: {
    fontFamily: "Inter_700Bold",
    fontSize: 24,
    color: "#000",
    marginBottom: 4,
  },
  userEmail: {
    fontFamily: "Inter_400Regular",
    fontSize: 14,
    color: "#666",
  },
  userCompany: {
    fontFamily: "Inter_400Regular",
    fontSize: 13,
    color: "#999",
    marginTop: 4,
  },
  menuSection: {
    backgroundColor: "transparent",
    marginBottom: 16,
  },
  menuItem: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 24,
    paddingHorizontal: 0,
    borderBottomWidth: 1,
    borderBottomColor: "rgba(0, 0, 0, 0.1)",
  },
  menuText: {
    fontFamily: "Inter_400Regular",
    fontSize: 16,
    color: "#000",
  },
  menuItemContent: {
    flex: 1,
  },
  menuSubtext: {
    fontFamily: "Inter_400Regular",
    fontSize: 13,
    color: "#999",
    marginTop: 2,
  },
  menuTextDanger: {
    fontFamily: "Inter_400Regular",
    fontSize: 16,
    color: "#D32F2F",
  },
  logoutButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 16,
    gap: 8,
  },
  logoutText: {
    fontFamily: "Inter_400Regular",
    fontSize: 16,
    color: "#D32F2F",
  },
  modalContainer: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
  },
  modalOverlay: {
    flex: 1,
    justifyContent: "center",
    padding: 24,
  },
  modalContent: {
    backgroundColor: "#FFF",
    borderRadius: 20,
    padding: 24,
    width: '100%',
    maxHeight: '80%',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  modalTitle: {
    fontFamily: "Inter_700Bold",
    fontSize: 20,
    color: "#000",
    marginBottom: 16,
    textAlign: "center",
  },
  modalDescription: {
    fontFamily: "Inter_400Regular",
    fontSize: 14,
    color: "#666",
    lineHeight: 20,
    marginBottom: 24,
  },
  input: {
    backgroundColor: "#F8F8F8",
    borderRadius: 12,
    paddingVertical: 12,
    paddingHorizontal: 16,
    fontFamily: "Inter_400Regular",
    fontSize: 16,
    color: "#000",
    marginBottom: 12,
  },
  inputLabel: {
    fontFamily: "Inter_600SemiBold",
    fontSize: 14,
    color: "#000",
    marginBottom: 8,
    marginTop: 8,
  },
  selectInput: {
    backgroundColor: "#F8F8F8",
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 12,
  },
  selectInputText: {
    fontFamily: "Inter_400Regular",
    fontSize: 16,
    color: "#999",
  },
  selectInputTextSelected: {
    color: "#000",
  },
  modalButtons: {
    flexDirection: "row",
    gap: 12,
    marginTop: 12,
  },
  cancelButton: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 12,
    backgroundColor: "#F0F0F0",
    alignItems: "center",
  },
  cancelButtonText: {
    fontFamily: "Inter_400Regular",
    fontSize: 16,
    color: "#666",
  },
  saveButton: {
    flex: 1,
  },
  saveButtonGradient: {
    paddingVertical: 14,
    borderRadius: 12,
    alignItems: "center",
  },
  saveButtonText: {
    fontFamily: "Inter_700Bold",
    fontSize: 16,
    color: "#FFF",
  },
  deleteButton: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 12,
    backgroundColor: "#D32F2F",
    alignItems: "center",
  },
  deleteButtonDisabled: {
    opacity: 0.7,
  },
  deleteButtonText: {
    fontFamily: "Inter_700Bold",
    fontSize: 16,
    color: "#FFF",
  },
  modalItem: {
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#F0F0F0",
  },
  modalItemText: {
    fontFamily: "Inter_400Regular",
    fontSize: 16,
    color: "#333",
  },
  fullModalContainer: {
    flex: 1,
    backgroundColor: '#FFF',
  },
  fullModalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E0E0E0',
  },
  fullModalTitle: {
    fontFamily: "Inter_700Bold",
    fontSize: 20,
    color: "#000",
  },
  versionText: {
    fontFamily: "Inter_400Regular",
    fontSize: 12,
    color: "#999",
    textAlign: "center",
    marginTop: 24,
    marginBottom: 8,
  },
});
